import React, { useEffect, useState } from 'react';
import { Close } from "@mui/icons-material";
import api from '../../services/api';

interface ProjetoFormProps {
  projeto?: any;
  onClose: () => void;
  onSaved: () => void;
}

const ProjetoForm: React.FC<ProjetoFormProps> = ({ projeto, onClose, onSaved }) => {
  const [nome, setNome] = useState(projeto?.nome || '');
  const [descricao, setDescricao] = useState(projeto?.descricao || '');
  const [dataInicio, setDataInicio] = useState(projeto?.data_inicio ? String(projeto.data_inicio).slice(0, 10) : '');
  const [dataFim, setDataFim] = useState(projeto?.data_fim ? String(projeto.data_fim).slice(0, 10) : '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const [exigeValidacao, setExigeValidacao] = useState(!!projeto?.exige_validacao);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(false);
  const [validadoresSelecionados, setValidadoresSelecionados] = useState<string[]>(
    (projeto?.usuarioVinculos || []).map((v: any) => v.usuario?.uid_usuario).filter(Boolean)
  );

  useEffect(() => {
    if (!exigeValidacao || usuarios.length > 0) return;
    setCarregandoUsuarios(true);
    api.get('/pesca/usuarios').then((res) => setUsuarios(res.data.data)).finally(() => setCarregandoUsuarios(false));
  }, [exigeValidacao, usuarios.length]);

  const toggleValidador = (uid_usuario: string) => {
    setValidadoresSelecionados((prev) =>
      prev.includes(uid_usuario) ? prev.filter((u) => u !== uid_usuario) : [...prev, uid_usuario]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setErro('Informe o nome do projeto.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const payload = {
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        data_inicio: dataInicio || null,
        data_fim: dataFim || null,
        exige_validacao: exigeValidacao,
        // Só manda a lista quando a validação está ligada — desligar o
        // toggle não apaga os validadores já marcados (podem reaparecer se
        // ligar de novo depois), backend já trata assim.
        ...(exigeValidacao ? { validadores: validadoresSelecionados } : {}),
      };

      if (projeto?.uid_pesca_projeto) {
        await api.put(`/pesca/projetos/${projeto.uid_pesca_projeto}`, payload);
      } else {
        await api.post('/pesca/projetos', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar projeto.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {projeto ? 'Editar Projeto' : 'Novo Projeto'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Nome *</label>
            <input style={inputStyle} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Piloto SP" />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Descrição</label>
            <textarea style={{ ...inputStyle, minHeight: '60px' }} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Data Início</label>
              <input type="date" style={inputStyle} value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Data Fim (encerramento)</label>
              <input type="date" style={inputStyle} value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>

          <div style={{ ...fieldWrapStyle, borderTop: '1px solid #eee', paddingTop: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', color: '#444' }}>
              <input
                type="checkbox"
                checked={exigeValidacao}
                onChange={(e) => setExigeValidacao(e.target.checked)}
              />
              Exige validação de registros
            </label>

            {exigeValidacao && (
              <div style={{ marginTop: '10px' }}>
                <label style={labelStyle}>Validadores deste projeto</label>
                {carregandoUsuarios ? (
                  <p style={{ fontSize: '0.82rem', color: '#999', margin: 0 }}>Carregando usuários...</p>
                ) : usuarios.length === 0 ? (
                  <p style={{ fontSize: '0.82rem', color: '#999', margin: 0 }}>Nenhum usuário do painel encontrado.</p>
                ) : (
                  <div style={validadoresBoxStyle}>
                    {usuarios.map((u) => (
                      <label key={u.uid_usuario} style={validadorItemStyle}>
                        <input
                          type="checkbox"
                          checked={validadoresSelecionados.includes(u.uid_usuario)}
                          onChange={() => toggleValidador(u.uid_usuario)}
                        />
                        <span>{u.nome} <span style={{ color: '#999' }}>({u.email})</span></span>
                      </label>
                    ))}
                  </div>
                )}
                {!carregandoUsuarios && validadoresSelecionados.length === 0 && (
                  <p style={avisoValidadorStyle}>
                    Nenhum validador selecionado — nenhum usuário vai poder validar os registros deste projeto ainda.
                  </p>
                )}
              </div>
            )}
          </div>

          {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
            <button type="submit" disabled={salvando} style={primaryBtnStyle}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const validadoresBoxStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '180px', overflowY: 'auto',
  border: '1px solid #eee', borderRadius: '8px', padding: '6px 10px',
};
const validadorItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem',
  padding: '5px 0', color: '#444',
};
const avisoValidadorStyle: React.CSSProperties = {
  fontSize: '0.8rem', color: '#8a5a00', margin: '8px 0 0', padding: '8px 10px',
  background: '#fff6e6', border: '1px solid #f0d9a0', borderRadius: '8px',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};

export default ProjetoForm;
